import React, { useState, useEffect, useCallback, useRef } from "react";
import { db } from "@lib/firebase";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import { Button } from "@cakrawalajakarta/shared-components";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { AnimatePresence, motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faMapMarkerAlt,
  faUsers,
  faExclamationTriangle,
  faBed,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

const HOTEL_COLLECTION = "pendaftaranHotel2026";
const EVENT_ID = "JOTR2026";
const BOOKING_DEADLINE = "June 30, 2026";
const HOTEL_BOOKING_CLOSED = true;

const HOTELS = [
  // Wisma Berkat
  {
    id: "wisma-berkat-4bed",
    wisma: "Wisma Berkat",
    tipeKamar: "4 Bed & 5 Bed",
    kapasitas: "4-5 pax",
    jarakVenue: "2 KM",
    fasilitas: ["Indoor Bathroom", "Wifi", "TV", "Parking", "Heater"],
    harga: 500000,
    unitName: "Room",
    jumlahKamar: 25,
    isClosed: true,
  },
  // Wisma Indofood
  {
    id: "wisma-indofood-karna",
    wisma: "Wisma Indofood",
    tipeKamar: "Karna",
    kapasitas: "24 pax",
    jarakVenue: "2 KM",
    fasilitas: ["Living room", "Kitchen", "Cooking tools", "Parking", "Indoor Bathroom", "TV", "Swimming pool"],
    harga: 7000000,
    unitName: "Villa",
    jumlahKamar: 12,
  },
  {
    id: "wisma-indofood-badranaya",
    wisma: "Wisma Indofood",
    tipeKamar: "Badranaya",
    kapasitas: "24 pax",
    jarakVenue: "2 KM",
    fasilitas: ["Living room", "Kitchen", "Cooking tools", "Parking", "Indoor Bathroom", "TV", "Swimming pool"],
    harga: 7000000,
    unitName: "Villa",
    jumlahKamar: 12,
    isClosed: true,
  },
  {
    id: "wisma-indofood-srikandi",
    wisma: "Wisma Indofood",
    tipeKamar: "Srikandi",
    kapasitas: "25 pax",
    jarakVenue: "2 KM",
    fasilitas: ["Living room", "Kitchen", "Cooking tools", "Parking", "Indoor Bathroom", "TV", "Swimming pool"],
    harga: 7000000,
    unitName: "Villa",
    jumlahKamar: 12,
    isClosed: true,
  },
  {
    id: "wisma-indofood-srikresna",
    wisma: "Wisma Indofood",
    tipeKamar: "Sri Kresna",
    kapasitas: "6 pax",
    jarakVenue: "2 KM",
    fasilitas: ["Living room", "Kitchen", "Cooking tools", "Parking", "Indoor Bathroom", "TV", "Swimming pool"],
    harga: 2500000,
    unitName: "Villa",
    jumlahKamar: 3,
  },
  {
    id: "wisma-indofood-bahurekso",
    wisma: "Wisma Indofood",
    tipeKamar: "Bahurekso",
    kapasitas: "3-4 pax",
    jarakVenue: "2 KM",
    fasilitas: ["Living room", "Kitchen", "Cooking tools", "Parking", "Indoor Bathroom", "TV", "Swimming pool"],
    harga: 2000000,
    unitName: "Villa",
    jumlahKamar: 1,
    isClosed: true,
  },
  {
    id: "wisma-indofood-wismasalim",
    wisma: "Wisma Indofood",
    tipeKamar: "Wisma Salim",
    kapasitas: "8 pax",
    jarakVenue: "2 KM",
    fasilitas: ["Living room", "Kitchen", "Cooking tools", "Parking", "Indoor Bathroom", "TV", "Swimming pool"],
    harga: 2500000,
    unitName: "Villa",
    jumlahKamar: 4,
  },
  // Wisma Pondok Pemuda
  {
    id: "wisma-pondok-barak",
    wisma: "Wisma Pondok Pemuda",
    tipeKamar: "Barak",
    kapasitas: "1 pax",
    jarakVenue: "300 Meter",
    fasilitas: ["Sharing room", "Sharing Bathroom", "Parking", "Wifi", "Water Dispenser"],
    harga: 100000,
    isPerPerson: true,
    unitName: "Person",
    jumlahKamar: 96,
  },
  {
    id: "wisma-pondok-mandalawangi",
    wisma: "Wisma Pondok Pemuda",
    tipeKamar: "Mandala Wangi",
    kapasitas: "4 pax",
    jarakVenue: "300 Meter",
    fasilitas: ["Sharing Bathroom", "Parking", "Wifi", "Water Dispenser"],
    harga: 400000,
    unitName: "Room",
    jumlahKamar: 18,
  },
];

const WISMA_GROUPS = ["Wisma Berkat", "Wisma Indofood", "Wisma Pondok Pemuda"];
const HOTEL_BOOKING_NIGHTS = ["2026-07-24", "2026-07-25"];
const HOTEL_BY_ID = HOTELS.reduce((acc, hotel) => {
  acc[hotel.id] = hotel;
  return acc;
}, {});

const getHotelBookedUnits = (hotelId, jumlahOrang = 1) => {
  const hotel = HOTEL_BY_ID[hotelId];
  if (!hotel?.isPerPerson) return 1;
  const parsed = Number(jumlahOrang);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const formatHotelDate = (date) => date.toISOString().slice(0, 10);

const addHotelDays = (dateString, days) => {
  const date = new Date(`${dateString}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return "";
  date.setUTCDate(date.getUTCDate() + days);
  return formatHotelDate(date);
};

const getHotelStayNights = (checkInDate, jumlahMalam = 1) => {
  const nightCount = Math.max(1, Number(jumlahMalam) || 1);
  if (!checkInDate) return [];
  return Array.from({ length: nightCount }, (_, index) => addHotelDays(checkInDate, index)).filter(Boolean);
};

const getHotelBookedForStay = (hotelQuota, checkInDate, jumlahMalam = 1) => {
  const nights = getHotelStayNights(checkInDate, jumlahMalam);
  if (!nights.length) return hotelQuota?.booked || 0;
  return Math.max(0, ...nights.map((night) => hotelQuota?.nights?.[night] || 0));
};

const isHotelFullyUnavailable = (hotel, hotelQuota) => {
  if (HOTEL_BOOKING_CLOSED || hotel.isClosed) return true;
  return HOTEL_BOOKING_NIGHTS.every((night) => (hotelQuota?.nights?.[night] || 0) >= hotel.jumlahKamar);
};

export const Hotel = () => {
  const { user, getIdToken } = useAuth();
  const { showNotification } = useNotification();
  const [userData, setUserData] = useState(null);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [activeOrder, setActiveOrder] = useState(null);
  const [hotelOrders, setHotelOrders] = useState([]);
  const [hotelQuotas, setHotelQuotas] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [bookingStep, setBookingStep] = useState("select");
  const [jumlahMalam, setJumlahMalam] = useState(1);
  const [jumlahOrang, setJumlahOrang] = useState(1);
  const [checkInDate, setCheckInDate] = useState("");
  const [isSnapReady, setIsSnapReady] = useState(false);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    const checkSnap = () => {
      if (window.snap) { setIsSnapReady(true); } else { setTimeout(checkSnap, 500); }
    };
    checkSnap();
  }, []);

  const fetchUserDataAndOrders = useCallback(async () => {
    if (!user) { setInitialLoading(false); return; }
    try {
      const userSnap = await getDoc(doc(db, "users", user.uid));
      if (userSnap.exists() && isMounted.current) setUserData(userSnap.data());

      const ordersSnap = await getDocs(query(
        collection(db, HOTEL_COLLECTION),
        where("uid", "==", user.uid)
      ));
      const orders = [];
      ordersSnap.forEach((d) => orders.push({ id: d.id, ...d.data() }));
      if (isMounted.current) {
        setHotelOrders(orders);
        const active = orders.find(
          (o) => o.transaction_status === "pending" ||
                 o.transaction_status === "settlement" ||
                 o.transaction_status === "capture"
        ) || null;
        setActiveOrder(active);
      }
    } catch (err) {
      console.error("[Hotel] fetchUserDataAndOrders error:", err);
    } finally {
      if (isMounted.current) setInitialLoading(false);
    }
  }, [user]);

  const fetchQuotas = useCallback(async () => {
    try {
      const q = query(
        collection(db, HOTEL_COLLECTION),
        where("transaction_status", "in", ["settlement", "capture", "pending"])
      );
      const snapshot = await getDocs(q);
      const quotasCount = {};
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.hotelId) {
          const val = getHotelBookedUnits(data.hotelId, data.jumlahOrang);
          if (!quotasCount[data.hotelId]) {
            quotasCount[data.hotelId] = { booked: 0, nights: {} };
          }
          const nights = getHotelStayNights(data.checkInDate, data.jumlahMalam);
          if (nights.length) {
            nights.forEach((night) => {
              quotasCount[data.hotelId].nights[night] = (quotasCount[data.hotelId].nights[night] || 0) + val;
            });
          } else {
            quotasCount[data.hotelId].booked += val;
          }
        }
      });
      Object.values(quotasCount).forEach((quota) => {
        quota.booked = Math.max(quota.booked, ...Object.values(quota.nights));
      });
      if (isMounted.current) {
        setHotelQuotas(quotasCount);
      }
    } catch (err) {
      console.error("[Hotel] fetchQuotas error:", err);
    }
  }, []);

  useEffect(() => { 
    fetchUserDataAndOrders(); 
    fetchQuotas();
  }, [fetchUserDataAndOrders, fetchQuotas]);

  const isPaidOrder = activeOrder &&
    (activeOrder.transaction_status === "settlement" || activeOrder.transaction_status === "capture");

  const handleBayar = useCallback((snapToken, orderId) => {
    if (!snapToken || !window.snap) { showNotification("error", "Invalid payment token."); return; }
    window.snap.pay(snapToken, {
      onSuccess: async () => { 
        showNotification("success", "Hotel payment successful!"); 
        if (orderId) { try { await updateDoc(doc(db, HOTEL_COLLECTION, orderId), { transaction_status: "settlement" }); } catch (e) { console.error(e); } }
        fetchUserDataAndOrders(); 
      },
      onPending: async () => { 
        showNotification("warning", "Hotel payment pending."); 
        if (orderId) { try { await updateDoc(doc(db, HOTEL_COLLECTION, orderId), { transaction_status: "pending" }); } catch (e) { console.error(e); } }
        fetchUserDataAndOrders(); 
      },
      onError: () => { showNotification("error", "Hotel payment failed."); },
      onClose: () => { console.log("[Hotel] Snap closed."); },
    });
  }, [showNotification, fetchUserDataAndOrders]);

  const handleContinuePayment = useCallback(async (order) => {
    if (!isSnapReady) { showNotification("error", "Midtrans is being prepared, please wait."); return; }
    setLoading(true);
    setError(null);
    try {
      const token = await getIdToken();
      const { data } = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/hotel-create-transaction`,
        { orderId: order.orderId || order.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.status === "settlement") {
        showNotification("success", "Hotel order already paid!");
        fetchUserDataAndOrders();
      } else if (data.snapToken) {
        handleBayar(data.snapToken, order.orderId || order.id);
      } else {
        showNotification("error", "Failed to get payment token.");
      }
    } catch (err) {
      setError(`Failed to continue payment: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [isSnapReady, getIdToken, handleBayar, showNotification, fetchUserDataAndOrders]);

  const handleConfirmBooking = useCallback(async () => {
    if (HOTEL_BOOKING_CLOSED) {
      showNotification("warning", "Hotel booking is closed.");
      return;
    }
    if (!selectedHotel || !user) return;
    if (!isSnapReady) { showNotification("error", "Midtrans is being prepared, please wait."); return; }

    if (!checkInDate) {
      setError("Please select a Check-in Date.");
      return;
    }

    // Final quota check before registration
    const currentFilled = getHotelBookedForStay(
      hotelQuotas[selectedHotel.id],
      checkInDate,
      jumlahMalam
    );
    const isSoldOut = currentFilled >= selectedHotel.jumlahKamar;
    if (isSoldOut) {
      setError("Sorry, the quota for this room type is full.");
      setBookingStep("select");
      setSelectedHotel(null);
      return;
    }

    if (!userData?.namaLengkap || !userData?.telepon || !userData?.email) {
      setError("Incomplete user data: Full Name, Phone, and Email must be filled in profile.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = await getIdToken();
      const { data } = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/hotel-register`,
        {
          uid: user.uid,
          hotelId: selectedHotel.id,
          wisma: selectedHotel.wisma,
          tipeKamar: selectedHotel.tipeKamar,
          kapasitas: selectedHotel.kapasitas,
          jarakVenue: selectedHotel.jarakVenue,
          fasilitas: selectedHotel.fasilitas,
          harga: selectedHotel.harga * (selectedHotel.isPerPerson ? jumlahOrang : 1) * jumlahMalam,
          hargaPerMalam: selectedHotel.harga * (selectedHotel.isPerPerson ? jumlahOrang : 1),
          jumlahMalam,
          jumlahOrang: selectedHotel.isPerPerson ? jumlahOrang : 1,
          checkInDate, // Tambahkan ini
          namaLengkap: userData?.namaLengkap || user.displayName || "",
          email: userData?.email || user.email || "",
          telepon: userData?.telepon || "",
          eventId: EVENT_ID,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchUserDataAndOrders();
      setBookingStep("select");
      setSelectedHotel(null);
      if (data.snapToken) handleBayar(data.snapToken, data.orderId);
    } catch (err) {
      setError(`Failed to book hotel: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedHotel, user, userData, jumlahMalam, jumlahOrang, checkInDate, hotelQuotas, isSnapReady, getIdToken, handleBayar, showNotification, fetchUserDataAndOrders]);

  const handleSelectHotel = (hotel) => {
    const isSoldOut = isHotelFullyUnavailable(hotel, hotelQuotas[hotel.id]);
    if (isSoldOut) return;
    if (!user) { setError("Please login first to book."); return; }
    if (activeOrder && !isPaidOrder) {
      setError("You already have an unpaid hotel order.");
      return;
    }
    if (activeOrder && isPaidOrder) {
      setError("You already have a paid hotel order.");
      return;
    }
    setError(null);
    setSelectedHotel(hotel);
    setJumlahMalam(1);
    setJumlahOrang(1);
    setBookingStep("confirm");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-lime-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <section className="min-h-screen bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">

        {/* Error Banner */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="bg-red-50 text-red-700 p-4 rounded-2xl border border-red-200 mb-6 flex items-center gap-3 shadow-none"
            >
              <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 shrink-0" />
              <span className="text-sm">{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex flex-col lg:flex-row gap-4 items-start mt-6">

          {/* ======================== Left Column ======================== */}
          <div className="w-full lg:flex-[2] flex flex-col gap-4">

            {/* Active Hotel Orders */}
            {hotelOrders
              .filter(o => ["settlement", "capture", "pending"].includes(o.transaction_status))
              .map((order) => {
                const isPaid = order.transaction_status === "settlement" || order.transaction_status === "capture";
                return (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl border border-slate-200 p-4"
                  >
                    <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4">Your Registration</h4>
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg md:text-xl font-bold text-slate-800 uppercase">
                          {order.wisma} — {order.tipeKamar}
                        </h3>
                        <div className="text-sm text-lime-600 mt-1 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                          Cibodas, West Java - Indonesia
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-12 gap-4 mb-4">
                      <div className="col-span-2 md:col-span-4">
                        <div className="text-xs text-slate-400 mb-1">Participant Name</div>
                        <div className="text-sm font-semibold text-slate-800 break-words">{order.namaLengkap || "-"}</div>
                      </div>
                      <div className="col-span-1 md:col-span-4">
                        <div className="text-xs text-slate-400 mb-1">Capacity</div>
                        <div className="text-sm font-semibold text-slate-800">{order.kapasitas}</div>
                      </div>
                      <div className="col-span-1 md:col-span-4">
                        <div className="text-xs text-slate-400 mb-1">Nights</div>
                        <div className="text-sm font-semibold text-slate-800">{order.jumlahMalam || 1} night(s)</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-12 gap-4 py-4 border-t border-slate-100 mb-4">
                      <div className="col-span-2 md:col-span-4">
                        <div className="text-xs text-slate-400 mb-1">Order ID</div>
                        <div className="text-sm font-semibold text-slate-800 font-mono break-words">{order.orderId || order.id}</div>
                      </div>
                      <div className="col-span-1 md:col-span-4">
                        <div className="text-xs text-slate-400 mb-1">Check-in</div>
                        <div className="text-sm font-semibold text-slate-800">{order.checkInDate || "-"}</div>
                      </div>
                      <div className="col-span-1 md:col-span-4">
                        <div className="text-xs text-slate-400 mb-1">Status</div>
                        <div className={`text-sm font-semibold italic ${isPaid ? "text-lime-600" : "text-amber-500"}`}>
                          {isPaid ? "Paid" : "Pending Payment"}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}

            {/* Confirm Step */}
            <AnimatePresence>
              {bookingStep === "confirm" && selectedHotel && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="bg-white rounded-2xl border border-slate-200 p-4"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 tracking-tight">Confirm Booking</h3>
                      <p className="text-sm text-gray-500">{selectedHotel.wisma} — {selectedHotel.tipeKamar}</p>
                    </div>
                    <button
                      onClick={() => { setBookingStep("select"); setSelectedHotel(null); setError(null); }}
                      className="text-sm text-gray-400 hover:text-gray-600 self-start"
                    >
                      ← Cancel
                    </button>
                  </div>

                  <div className="mt-4 space-y-0">
                    {[
                      { label: "Wisma", value: selectedHotel.wisma },
                      { label: "Room Type", value: selectedHotel.tipeKamar },
                      { label: "Capacity", value: selectedHotel.kapasitas },
                      { label: "Distance to Venue", value: selectedHotel.jarakVenue },
                      { label: "Price/Night", value: `IDR ${selectedHotel.harga.toLocaleString("en-US")}` },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex items-start justify-between py-2.5 border-b border-gray-100 gap-4">
                        <p className="text-[11px] font-semibold text-gray-400 shrink-0">{label}</p>
                        <p className="text-sm font-medium text-gray-800 text-right">{value}</p>
                      </div>
                    ))}
                    <div className="flex flex-wrap gap-1 py-2.5 border-b border-gray-100">
                      {selectedHotel.fasilitas.map((f) => (
                        <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">{f}</span>
                      ))}
                    </div>
                  </div>

                  {/* Check-in Date & Jumlah Malam selector */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-[11px] font-semibold text-gray-400 mb-3 uppercase">CHECK-IN DATE</p>
                      <input
                        type="date"
                        value={checkInDate}
                        onChange={(e) => setCheckInDate(e.target.value)}
                        min={new Date().toISOString().split("T")[0]}
                        className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-lime-500 text-sm font-medium text-gray-800"
                      />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-gray-400 mb-3 uppercase">NUMBER OF NIGHTS</p>
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => setJumlahMalam(Math.max(1, jumlahMalam - 1))}
                          className="w-8 h-8 rounded-full border-2 border-lime-600 bg-white text-lime-600 flex items-center justify-center font-bold text-lg leading-none hover:bg-lime-50"
                        >−</button>
                        <span className="text-xl font-black text-gray-900 w-8 text-center">{jumlahMalam}</span>
                        <button
                          onClick={() => setJumlahMalam(jumlahMalam + 1)}
                          className="w-8 h-8 rounded-full border-2 border-lime-600 bg-white text-lime-600 flex items-center justify-center font-bold text-lg leading-none hover:bg-lime-50"
                        >+</button>
                        <span className="text-sm text-gray-400">night(s)</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Hotel Selection */}
            {bookingStep === "select" && !activeOrder && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="bg-white rounded-2xl border border-slate-200 p-4"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 tracking-tight">Book Accommodation</h3>
                    <p className="text-sm text-gray-500">Select wisma and room type for JOTR 2026</p>
                  </div>
                </div>

                {/* All hotels grouped by wisma — flat sections, no wrapper container */}
                <div className="space-y-6">
                  {WISMA_GROUPS.map((wismaName) => {
                    const groupHotels = HOTELS.filter(h => h.wisma === wismaName);
                    return (
                      <div key={wismaName}>
                        <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">{wismaName}</p>
                        <div className="space-y-4">
                          {groupHotels.map((hotel) => {
                            const currentFilled = hotelQuotas[hotel.id]?.booked || 0;
                            const isSoldOut = isHotelFullyUnavailable(hotel, hotelQuotas[hotel.id]);
                            return (
                              <div
                                key={hotel.id}
                                onClick={() => handleSelectHotel(hotel)}
                                className={`border border-slate-200 rounded-2xl overflow-hidden ${
                                  isSoldOut
                                    ? "opacity-50 cursor-not-allowed"
                                    : "cursor-pointer hover:border-slate-300 transition-colors"
                                }`}
                              >
                                {/* New Row with Divider and Different Color */}
                                <div className="bg-gray-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                                  <h4 className="text-lg font-bold text-gray-900">{hotel.tipeKamar}</h4>
                                  {isSoldOut && (
                                    <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-red-100 text-red-600 uppercase">
                                      {HOTEL_BOOKING_CLOSED || hotel.isClosed ? "CLOSED" : "SOLD"}
                                    </span>
                                  )}
                                </div>

                                {/* Original Card Content */}
                                <div className="bg-white px-4 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 md:grid md:grid-cols-12 md:gap-4">
                                  <div className="w-full sm:flex-1 md:col-span-6 min-w-0">
                                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-2">
                                      <span className="flex items-center gap-1">
                                        <FontAwesomeIcon icon={faUsers} className="text-gray-400" />
                                        {hotel.kapasitas}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400" />
                                        {hotel.jarakVenue}
                                      </span>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                      {hotel.fasilitas.slice(0, 4).map((f) => (
                                        <span key={f} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">{f}</span>
                                      ))}
                                      {hotel.fasilitas.length > 4 && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">+{hotel.fasilitas.length - 4}</span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="hidden md:flex md:col-span-3 flex-col items-center justify-center border-l border-slate-200 py-1 h-12 self-center">
                                    {HOTEL_BOOKING_CLOSED || hotel.isClosed ? (
                                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Closed</p>
                                    ) : (
                                      <p className="text-xs font-semibold text-gray-900">
                                        {currentFilled} <span className="text-gray-400 font-normal whitespace-nowrap">/ {hotel.jumlahKamar} Booked</span>
                                      </p>
                                    )}
                                  </div>

                                  <div className="w-full sm:w-auto flex justify-between sm:justify-end items-center sm:items-end flex-row sm:flex-col shrink-0 md:col-span-3 md:border-l border-slate-200 py-1 md:flex md:flex-col md:justify-center md:items-end md:text-right md:pl-6 h-12 self-center">
                                    <div className="md:hidden">
                                      {HOTEL_BOOKING_CLOSED || hotel.isClosed ? (
                                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Closed</p>
                                      ) : (
                                        <p className="text-xs font-medium text-gray-600">
                                          {currentFilled} / {hotel.jumlahKamar} booked
                                        </p>
                                      )}
                                    </div>
                                    <div className="text-right">
                                      <p className="text-xs text-gray-400">per {hotel.unitName || 'night'}</p>
                                      <p className="text-md font-black text-gray-900">
                                        {`IDR ${Number(hotel.harga).toLocaleString("en-US")}`}
                                      </p>
                                      {hotel.hargaNote && (
                                        <p className="text-xs text-gray-400">{hotel.hargaNote}</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </div>

          {/* ======================== Right Sidebar ======================== */}
          <div className="w-full lg:flex-1 lg:max-w-md space-y-4">
            <div className="sticky top-12 space-y-4">

              {/* Order Summary */}
              <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-none">
                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-4">Order Summary</h4>

                {bookingStep === "confirm" && selectedHotel ? (
                  <>
                    <div className="flex items-start justify-between py-2.5 border-b border-gray-100">
                      <p className="text-[11px] font-semibold text-gray-400">Wisma</p>
                      <p className="text-sm font-medium text-gray-800 text-right">{selectedHotel.wisma}</p>
                    </div>
                    <div className="flex items-start justify-between py-2.5 border-b border-gray-100">
                      <p className="text-[11px] font-semibold text-gray-400">Room Type</p>
                      <p className="text-sm font-medium text-gray-800 text-right">{selectedHotel.tipeKamar}</p>
                    </div>
                    {checkInDate && (
                      <div className="flex items-start justify-between py-2.5 border-b border-gray-100">
                        <p className="text-[11px] font-semibold text-gray-400">Check-in Date</p>
                        <p className="text-sm font-medium text-gray-800 text-right">{checkInDate}</p>
                      </div>
                    )}
                    <div className="flex items-start justify-between py-2.5 border-b border-gray-100">
                      <p className="text-[11px] font-semibold text-gray-400">Number of Nights</p>
                      <p className="text-sm font-medium text-gray-800">{jumlahMalam} night(s)</p>
                    </div>
                    {checkInDate && (
                      <div className="flex items-start justify-between py-2.5 border-b border-gray-100">
                        <p className="text-[11px] font-semibold text-gray-400">Available</p>
                        <p className="text-sm font-medium text-gray-800">
                          {Math.max(
                            0,
                            selectedHotel.jumlahKamar -
                              getHotelBookedForStay(hotelQuotas[selectedHotel.id], checkInDate, jumlahMalam)
                          )} {selectedHotel.unitName || "unit"}(s)
                        </p>
                      </div>
                    )}
                    {selectedHotel.isPerPerson && (
                      <div className="flex items-start justify-between py-2.5 border-b border-gray-100">
                        <p className="text-[11px] font-semibold text-gray-400">Number of People</p>
                        <p className="text-sm font-medium text-gray-800">{jumlahOrang} people</p>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-3 mt-1">
                      <p className="text-[11px] font-semibold text-gray-400">Total</p>
                      <p className="text-lg font-bold text-gray-900">
                        IDR {(selectedHotel.harga * (selectedHotel.isPerPerson ? jumlahOrang : 1) * jumlahMalam).toLocaleString("en-US")}
                        <span className="text-xs font-normal text-gray-400 ml-1">/ {selectedHotel.unitName ? selectedHotel.unitName : 'booking'}</span>
                      </p>
                    </div>
                    {/* Payment Button inside summary for new booking */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <Button
                        onClick={handleConfirmBooking}
                        disabled={loading}
                        className="w-full text-sm font-bold rounded-xl shadow-none"
                      >
                        {loading ? "Processing..." : `Book & Pay — IDR ${(selectedHotel.harga * (selectedHotel.isPerPerson ? jumlahOrang : 1) * jumlahMalam).toLocaleString("en-US")}`}
                      </Button>
                    </div>
                  </>
                ) : activeOrder ? (
                  <>
                    <div className="flex items-start justify-between py-2.5 border-b border-gray-100">
                      <p className="text-[11px] font-semibold text-gray-400">Wisma</p>
                      <p className="text-sm font-medium text-gray-800 text-right">{activeOrder.wisma}</p>
                    </div>
                    <div className="flex items-start justify-between py-2.5 border-b border-gray-100">
                      <p className="text-[11px] font-semibold text-gray-400">Room Type</p>
                      <p className="text-sm font-medium text-gray-800 text-right">{activeOrder.tipeKamar}</p>
                    </div>
                    <div className="flex items-start justify-between py-2.5 border-b border-gray-100">
                      <p className="text-[11px] font-semibold text-gray-400">Check-in Date</p>
                      <p className="text-sm font-medium text-gray-800 text-right">{activeOrder.checkInDate || "-"}</p>
                    </div>
                    <div className="flex items-start justify-between py-2.5 border-b border-gray-100">
                      <p className="text-[11px] font-semibold text-gray-400">Number of Nights</p>
                      <p className="text-sm font-medium text-gray-800">{activeOrder.jumlahMalam || 1} night(s)</p>
                    </div>
                    <div className="flex items-center justify-between pt-3 mt-1">
                      <p className="text-[11px] font-semibold text-gray-400">Total</p>
                      <p className="text-lg font-bold text-gray-900">
                        IDR {Number(activeOrder.harga).toLocaleString("en-US")}
                      </p>
                    </div>
                    {/* Payment Button inside summary */}
                    {!isPaidOrder && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <Button
                          onClick={() => handleContinuePayment(activeOrder)}
                          disabled={loading}
                          className="w-full text-sm font-bold rounded-xl shadow-none"
                        >
                          {loading ? "Processing..." : "Continue Payment"}
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-300 py-2">No selection yet</p>
                )}
              </div>

              {/* Ketentuan */}
              <div className="bg-gray-50 rounded-2xl border border-slate-200 p-4 shadow-none">
                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-4">Terms & Conditions</h4>
                <ul className="space-y-3">
                  {[
                    `Max booking deadline: ${BOOKING_DEADLINE}`,
                    "Price listed is per night",
                    "Confirm availability with PIC",
                    "Non-refundable after payment",
                  ].map((text, i) => (
                    <li key={i} className="flex items-start gap-3 text-[13px] text-gray-600">
                      <FontAwesomeIcon icon={faCheckCircle} size="xs" className="text-lime-600 mt-0.5 shrink-0" />
                      {text}
                    </li>
                  ))}
                </ul>
              </div>

              {/* PIC */}
              <div className="bg-gray-50 rounded-2xl border border-slate-200 p-4 shadow-none">
                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-4">Accommodation PIC</h4>
                <div className="space-y-3">
                  {[
                    { name: "Windra", phone: "082123334246" },
                  ].map(({ name, phone }) => (
                    <div key={name} className="flex items-center justify-between">
                      <span className="text-[13px] text-gray-600">{name}</span>
                      <a href={`tel:${phone}`} className="text-xs font-mono text-lime-600 font-bold">{phone}</a>
                    </div>
                  ))}
                </div>
              </div>


            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hotel;
